// import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import Pagination from "../components/Pagination";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import DropdownSelect from "../components/DropdownSelect";
import { AuthContext } from "../store/ContextStore";
import api from "../utils/api";

import { PLAYER_GAME_LOGS, PLAYER_PROFILE, PLAYER_RECORDS, PLAYER_SEASON_AND_CAREER_HIGHS, PLAYER_STREAKS, PLAYER_STREAKS_MAPPING } from "../constants/routes";
import { useNavigate, useParams } from "react-router-dom";
import Pill from "../components/Pill";
import Modal from "../components/modals/Modal";
import dashboardServices from '../services/NlpSearchService';
import NLPSearch from "../components/NLPSearch";
import Spinner from "../components/Spinner";
import SearchWithSuggestions from "../components/SearchWithSuggestion";
import Datepicker from "../components/Datepicker";
import Table from "../components/Table";
import NoDataFound from "../components/NoDataFound";
import Button from "../components/Button";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ColDef, ColGroupDef, ModuleRegistry, themeBalham, themeQuartz } from 'ag-grid-community';
import { useReactiveRef } from '../utils/UseReactiveState';
import localStorageService from '../services/localStorageService';
import { useReportIssue } from '../hooks/useReportIssue'; 
import { ReportIssueModal } from '../components/ReportIssueModal';
import { exportToXLSX } from "../utils/xlsx";
import { useCareerPlayerIds } from "../hooks/useCareerPlayerIds";

  
ModuleRegistry.registerModules([AllCommunityModule]);

type TabCode = "stats" | "records" | "season&highs" | "logs" | "streks";
interface PlayerData {
  PlayerName: string,
  SportCode: string,
  TeamName: string,
  TeamCode: string,
  PlayerPosition: string,
  stat_mapping: {
    [key: string | number]: any;
  }
}

export interface CareerTeamProfile {
  playerId: string;
  PlayerName: string;
  TeamName: string;
  TeamCode: string;
  PlayerPosition: string;
  stat_mapping: {
    [key: string | number]: any;
  };
}

const PlayerDashboard = () => {
  const [activeTab, setActiveTab] = useState<TabCode>("stats");
  const { team } = useContext(AuthContext);
  const [playerData, setPlayerData] = useState<PlayerData | null | undefined>();
  const [careerProfiles, setCareerProfiles] = useState<CareerTeamProfile[]>([]);
  const { playerId, sportsCode } = useParams();
  const careerPlayerIds = useCareerPlayerIds();
  
  const tabs: { tabName: string, tabCode: TabCode ,  errorContext: string;}[] = [
    { tabName: "Player Statistics", tabCode: "stats" ,    errorContext: "Error fetching player statistics.",
},
    { tabName: "Player Records", tabCode: "records",    errorContext: "Error fetching player records.",
 },
    { tabName: "Season and Career Highs", tabCode: "season&highs",      errorContext: "Error fetching season and career highs.",
 },
    { tabName: "Player Game Log", tabCode: "logs",    errorContext: "Error fetching player game logs.",
 },
    { tabName: "Player Streaks", tabCode: "streks",    errorContext: "Error fetching player streak data.",
 },
  ];

  const handleTabChange = (tabCode: TabCode) => {
    
    setActiveTab(tabCode);
  }

  const teamLogoUrl = (teamCode: string | number | undefined | null) => {
    const code = Math.round(parseInt(String(teamCode ?? "0"), 10));
    if (!code || Number.isNaN(code)) return null;
    return `https://premier.athlytesports.com/assets/images/teams/${code}.png`;
  };

  const headerTeamLogos = (() => {
    const seen = new Set<string>();
    const logos: { url: string; name: string }[] = [];
    for (const profile of careerProfiles) {
      const url = teamLogoUrl(profile.TeamCode);
      const key = String(profile.TeamCode ?? profile.TeamName);
      if (!url || seen.has(key)) continue;
      seen.add(key);
      logos.push({ url, name: profile.TeamName || "" });
    }
    if (logos.length === 0) {
      const fallback = teamLogoUrl(playerData?.TeamCode);
      if (fallback) {
        logos.push({ url: fallback, name: playerData?.TeamName || "" });
      }
    }
    return logos;
  })();


  // Fetch parent team profile first, then each transfer school id (backend: one Player_id per call)
useEffect(() => {
  async function fetchPlayerBasicInfo() {
    if (!sportsCode || careerPlayerIds.length === 0) return;
    
    try {
      const profiles: CareerTeamProfile[] = [];
      for (const id of careerPlayerIds) {
        const res = await api.post(PLAYER_PROFILE, {
          Player_id: id,
          SportCode: sportsCode,
        });
        profiles.push({
          playerId: id,
          PlayerName: res.data.PlayerName,
          TeamName: res.data.TeamName,
          TeamCode: res.data.TeamCode || '',
          PlayerPosition: res.data.PlayerPosition || '',
          stat_mapping: res.data.stat_mapping || {},
        });
      }

      setCareerProfiles(profiles);
      const primary = profiles[0];
      const teamNames = profiles
        .map((p) => p.TeamName)
        .filter(Boolean)
        .filter((name, i, arr) => arr.indexOf(name) === i);

      setPlayerData({
        PlayerName: primary?.PlayerName || '',
        SportCode: sportsCode,
        TeamName: teamNames.length > 1 ? teamNames.join(" / ") : (primary?.TeamName || ''),
        TeamCode: primary?.TeamCode || '',
        PlayerPosition: primary?.PlayerPosition || '',
        stat_mapping: primary?.stat_mapping || {},
      });
      
    } catch (error) {
      console.error("Error fetching basic player info:", error);
      setCareerProfiles([]);
      setPlayerData({
        PlayerName: '',
        SportCode: sportsCode,
        TeamName: '',
        TeamCode: '',
        PlayerPosition: '',
        stat_mapping: {}
      });
    }
  }

  fetchPlayerBasicInfo();
}, [careerPlayerIds.join(","), sportsCode]);
  return (
    <div className="overflow-x-auto w-full flex flex-col gap-2">
      <div className=""
        style={{
          background: `${team.colorCode} url('/playerbanner.png') center/cover no-repeat`,
        }}
      >
        <div className="flex flex-col gap-2 w-full px-[54px] py-6">
          <div className="flex flex-row gap-4 mt-16 text-white">
            <div className="flex flex-col justify-end">
              <div className="flex flex-row items-center gap-4">
                <div className="flex flex-row items-center gap-2">
                  {headerTeamLogos.map((logo) => (
                    <img
                      key={logo.url}
                      src={logo.url}
                      alt={logo.name || "Team"}
                      title={logo.name}
                      className="w-[3.5rem] h-[3.5rem] object-contain rounded-lg bg-white/10"
                    />
                  ))}
                </div>
                <div className="flex flex-row items-center gap-2">
                  <p>{playerData?.TeamName}</p>
                </div>
              </div>
              <p>{playerData?.PlayerName} ({playerData?.PlayerPosition})</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2 px-[54px]" style={{ backgroundColor: "rgba(0, 0, 0, 0.2)" }}>
          {tabs.map((tab) => (
            <div key={tab.tabCode}>
              <button
                onClick={() => handleTabChange(tab.tabCode)}
                className={`px-4 py-2 transition-all duration-300 text-white font-primary font-normal text-xs ${activeTab === tab.tabCode ? "border-b-2" : "hover:bg-gray-300"}`}
              >
                {tab.tabName.toUpperCase()}
              </button>
            </div>
          ))}
        </div>
      </div>
      <div className="px-[54px] py-[38px]">
        {activeTab === "stats" && (
          <StatTable
            setPlayerData={setPlayerData}
            careerProfiles={careerProfiles}
          />
        )}
        {activeTab === "records" && (
          <RecordsView playerData={playerData} careerProfiles={careerProfiles} />
        )}
        {activeTab === "season&highs" && (
          <SeasonAndHighsView playerData={playerData} careerProfiles={careerProfiles} />
        )}
        {activeTab === "logs" && (
          <GameLogsView playerData={playerData} careerProfiles={careerProfiles} />
        )}
        {activeTab === "streks" && (
          <StreaksView playerData={playerData} careerProfiles={careerProfiles} />
        )}

      </div>
    </div>
  );
};

export default PlayerDashboard;


interface IStats {
  stats: { [key: string]: any };
  stat_mapping: {
    [key: string]: {
      [key: string]: string,
    }
  }
}

interface IFilters {
  seasons: string[],
  substat: string[],
  stat: string[],
  teams: string[],
};

interface PlayerProfileRes {
  season: string;
  PlayerName: string;
  TeamName: string;
  stats: {
    [key: string]: string;
  }[];
  stat_mapping: {
    // category
    [key: string]: string[]
  }
}

const COLS_PER_PAGE = 8; 
const StatTable = ({
  setPlayerData,
  careerProfiles,
}: {
  setPlayerData: any;
  careerProfiles: CareerTeamProfile[];
}) => {  
  const [selectedFilters, setSelectedFilters] = useState<IFilters>({ seasons: [], substat: [], stat: [], teams: [] });
  const [playerProfileData, setPlayerProfileData] = useState<PlayerProfileRes | null>(null);

  const [isLoading, setisLoading] = useState(false);

  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const { playerId, sportsCode } = useParams();
  const careerPlayerIds = useCareerPlayerIds();
  const isMultiTeam = careerPlayerIds.length > 1;

  function handleDropdownChange(key: any, value: any) {
    const updatedFilters: any = { ...selectedFilters };
    if (key === "seasons" || key === "substat" || key === "stat" || key === "teams") {
      updatedFilters[key] = updatedFilters[key].includes(value)
        ? updatedFilters[key].filter((item: any) => item !== value)
        : [...updatedFilters[key], value];
    }

    setSelectedFilters(updatedFilters);
    if (!playerProfileData) return;
  }

  const getTotalRow = () => {
  if (!playerProfileData || !playerProfileData.stats?.length) return null;

  const totalRow: any = { season: "TOTAL" };
  if (isMultiTeam) {
    totalRow.TEAM = "";
  }

  const relevantStats = playerProfileData.stats.filter((stat) => {
    const teamOk =
      !isMultiTeam ||
      selectedFilters.teams.length === 0 ||
      selectedFilters.teams.includes((stat as any).TEAM);
    const seasonOk =
      selectedFilters.seasons.length === 0 ||
      selectedFilters.seasons.includes(stat.season);
    return teamOk && seasonOk;
  });

  // If no data after filtering, don't show total
  if (relevantStats.length === 0) return null;

  const statKeys = Object.keys(relevantStats[0] ?? {}).filter(
    (key) => key !== "season" && key !== "TEAM"
  );

  for (const key of statKeys) {
    const isPercentageStat = String(key).includes("%");

    const { sum, count } = relevantStats.reduce(
      (acc, row) => {
        const val = row[key];
        const numericVal =
          typeof val === "number"
            ? val
            : typeof val === "string"
            ? parseFloat(val)
            : NaN;

        if (!isNaN(numericVal)) {
          acc.sum += numericVal;
          acc.count += 1;
        }
        return acc;
      },
      { sum: 0, count: 0 }
    );

    if (isPercentageStat) {
      totalRow[key] = count > 0 ? sum / count : 0;
    } else {
      totalRow[key] = sum;
    }
  }

  return totalRow;
};

const handleDeleteFilter = (filter: string) => {
   console.log('Filter to delete:', filter, typeof filter);
  console.log('Current seasons:', selectedFilters.seasons);

  if (selectedFilters?.teams?.includes(filter)) {
    setSelectedFilters({
      ...selectedFilters,
      teams: selectedFilters.teams.filter((team) => team !== filter),
    });
    return;
  }

  // Check if deleting a season

    const seasonExists = selectedFilters?.seasons?.some(season => String(season) === String(filter));
  
  if (seasonExists) {
    setSelectedFilters({ 
      ...selectedFilters, 
      seasons: selectedFilters.seasons.filter((season) => String(season) !== String(filter)) 
    });
    return;
  }
  if (selectedFilters?.seasons?.includes(filter)) {
    setSelectedFilters({ 
      ...selectedFilters, 
      seasons: selectedFilters.seasons.filter((season) => String(season) !== String(filter)) 
    });
    return;
  }

  // Check if deleting a category (stat)
  const isStatCategory = selectedFilters?.stat?.includes(filter);
  
  if (isStatCategory) {
    // Get the stats in this category
    const categoryStats = playerProfileData?.stat_mapping?.[filter] || [];
    
    // Remove the category
    const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
    
    // Remove ALL substats that belong to this category
    const updatedSubstat = selectedFilters.substat.filter(
      (substat: string) => !categoryStats.includes(substat)
    );
    
    setSelectedFilters({
      ...selectedFilters,
      stat: updatedStat,
      substat: updatedSubstat
    });
    return;
  }

  // Check if deleting a substat
  if (selectedFilters?.substat?.includes(filter)) {
    setSelectedFilters({
      ...selectedFilters,
      substat: selectedFilters.substat.filter((s: string) => s !== filter)
    });
    return;
  }
};
  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);
    setSelectedFilters((prevFilters: any) => {
      const updatedFilters = { ...prevFilters, DATE_MIN: newDate };

      if (endDate && new Date(newDate) > new Date(endDate)) {
        setEndDate(null);
        delete updatedFilters["DATE_MAX"];
      }
      return updatedFilters;
    });
  };

  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
    setSelectedFilters((prevFilters: any) => ({
      ...prevFilters,
      DATE_MAX: newDate,
    }));
  };

  const filteredStatKeys = useMemo(() => {
    if (!playerProfileData || !playerProfileData.stats?.length) return [];

    const allStatsFlat = selectedFilters.stat
      .map((stat: string) => playerProfileData.stat_mapping?.[stat] ?? [])
      .flat();

    const totalRow = getTotalRow();

    let keys = Object.keys(playerProfileData.stats[0] ?? {}).filter(
      (key) => key !== "season" && key !== "TEAM"
    );

    // Apply filters only if filters are selected
    if (selectedFilters.stat.length > 0) {
      keys = keys.filter((key) => allStatsFlat.includes(key));
    }
    
    if (selectedFilters.substat.length > 0) {
      keys = keys.filter((key) => selectedFilters.substat.includes(key));
    }

    // Remove columns whose total is 0
    keys = keys.filter((key) => {
      if (!totalRow) return true;
      const val = totalRow[key];
      const numericVal =
        typeof val === "number" ? val : typeof val === "string" ? parseFloat(val) : 0;
      return numericVal !== 0;
    });

    // Sort by total value
    if (totalRow) {
      keys.sort((a, b) => {
        const valA = totalRow[a];
        const valB = totalRow[b];
        const numericA = typeof valA === "number" ? valA : typeof valA === "string" ? parseFloat(valA) : 0;
        const numericB = typeof valB === "number" ? valB : typeof valB === "string" ? parseFloat(valB) : 0;
        return numericB - numericA;
      });
    }

    return keys;
  }, [playerProfileData, selectedFilters, getTotalRow]);

  const getColDefs = () => {
    if (!playerProfileData || !playerProfileData.stats?.length) return [];

    const formatValue = (val: any) => {
      if (val === null || val === undefined) return "-";
      const num = typeof val === "number" ? val : parseFloat(String(val));
      if (isNaN(num)) return String(val);
      return Number.isInteger(num) ? String(num) : num.toFixed(2);
    };

    return [
      {
        field: "season",
        headerName: "SEASON",
        pinned: 'left',
        width: 120,
        sortable: true,
        valueFormatter: (params: any) => params.value || "-",
      },
      ...(isMultiTeam
        ? [
            {
              field: "TEAM",
              headerName: "TEAM",
              pinned: "left" as const,
              width: 140,
              sortable: true,
              valueFormatter: (params: any) => params.value || "-",
            },
          ]
        : []),
      ...filteredStatKeys.map((key) => ({
        field: key,
        headerName: key,
        width: 120,
        sortable: true,
        valueFormatter: (params: any) => formatValue(params.value),
      })),
    ] as any;
  };

  useEffect(() => {
    async function fetchData() {
      if (!sportsCode || careerPlayerIds.length === 0) return;
      setisLoading(true);

      try {
        const mergedStats: any[] = [];
        let mergedMapping: Record<string, any> = {};
        let primaryProfile: PlayerProfileRes | null = null;
        const teamNames: string[] = [];

        // Parent / logged-in school first, then each transfer id (one Player_id per request)
        for (const id of careerPlayerIds) {
          const res = await api.post(PLAYER_PROFILE, {
            Player_id: id,
            SportCode: sportsCode,
          });
          const profile: PlayerProfileRes = res.data;
          if (!primaryProfile) {
            primaryProfile = profile;
          }
          if (profile.TeamName) {
            teamNames.push(profile.TeamName);
          }
          const seasonRows = Array.isArray(profile.stats) ? profile.stats : [];
          for (const row of seasonRows) {
            mergedStats.push({
              ...row,
              ...(isMultiTeam ? { TEAM: profile.TeamName || "" } : {}),
            });
          }
          if (profile.stat_mapping && typeof profile.stat_mapping === "object") {
            mergedMapping = { ...mergedMapping, ...profile.stat_mapping };
          }
        }

        const uniqueTeams = teamNames.filter(
          (name, i, arr) => arr.indexOf(name) === i
        );

        const playerProfileRes: PlayerProfileRes = {
          ...(primaryProfile as PlayerProfileRes),
          stats: mergedStats,
          stat_mapping:
            Object.keys(mergedMapping).length > 0
              ? mergedMapping
              : primaryProfile?.stat_mapping || {},
          TeamName:
            uniqueTeams.length > 1
              ? uniqueTeams.join(" / ")
              : primaryProfile?.TeamName || "",
        };

        setPlayerProfileData(playerProfileRes);

        if (primaryProfile && setPlayerData) {
          setPlayerData({
            PlayerName: primaryProfile.PlayerName,
            SportCode: sportsCode,
            TeamName: playerProfileRes.TeamName,
            TeamCode: (primaryProfile as any).TeamCode || careerProfiles[0]?.TeamCode || "",
            PlayerPosition:
              (primaryProfile as any).PlayerPosition ||
              careerProfiles[0]?.PlayerPosition ||
              "",
            stat_mapping: playerProfileRes.stat_mapping || {},
          });
        }

        setErrorStatus(null);
        setErrorMessage(null);

      } catch (error: any) {
        console.error("Error fetching player profile:", error);

        const status = error?.response?.status ?? 0;
        setErrorStatus(status);

        if (status >= 500) {
          setErrorMessage("Failed to load player profile.");
        } else {
          setErrorMessage("Something went wrong.");
        }

      } finally {
        setisLoading(false);
      }
    }

    fetchData();
  }, [careerPlayerIds.join(","), sportsCode]);

  const reportIssue = useReportIssue();

  const handleReportIssue = (errorContext?: string) => {
    console.log('Report issue clicked!');
    reportIssue.openModal({
      ComponentSource: 'PlayerDashboard - StatTable',
      ErrorContext: errorContext || errorMessage || '',
      UserContext: {
        playerId,
        sportsCode,
        errorStatus,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({ seasons: [], substat: [], stat: [], teams: [] });
  };

  useEffect(() => {
    console.log("Selected Filters:", selectedFilters);
  }, [selectedFilters]);

  const teamFilterOptions = useMemo(() => {
    const fromStats = (playerProfileData?.stats ?? [])
      .map((stat: any) => stat.TEAM)
      .filter(Boolean);
    const fromProfiles = careerProfiles.map((p) => p.TeamName).filter(Boolean);
    return [...new Set([...fromProfiles, ...fromStats])];
  }, [playerProfileData, careerProfiles]);

  // Filter data based on selected teams + seasons
  const filteredData = (playerProfileData?.stats ?? []).filter((stat: any) => {
    const teamOk =
      !isMultiTeam ||
      selectedFilters.teams.length === 0 ||
      selectedFilters.teams.includes(stat.TEAM);
    const seasonOk =
      selectedFilters.seasons.length === 0 ||
      selectedFilters.seasons.includes(stat.season);
    return teamOk && seasonOk;
  });

  const seasonFilterOptions = useMemo(() => {
    const rows = (playerProfileData?.stats ?? []).filter((stat: any) =>
      !isMultiTeam ||
      selectedFilters.teams.length === 0 ||
      selectedFilters.teams.includes(stat.TEAM)
    );
    return [...new Set(rows.map((stat: any) => stat.season).filter(Boolean))];
  }, [playerProfileData, selectedFilters.teams, isMultiTeam]);

  const totalRow = getTotalRow();

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-3 w-full">
        <div className="flex flex-row flex-wrap items-center gap-2 w-full">
          <div className="text-sm font-medium shrink-0">Filters:-</div>

          <div className="flex gap-2 flex-wrap items-center flex-1 min-w-0">
            {[
              ...selectedFilters.teams,
              ...selectedFilters.stat,
              ...selectedFilters.seasons,
              ...selectedFilters.substat,
            ].map((filter, index) => (
                <Pill
                  key={index}
                  text={String(filter)}
                  showCross={true}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteFilter(String(filter));
                  }}
                />
              )
            )}
            {(selectedFilters.teams.length ||
              selectedFilters.stat.length ||
              selectedFilters.seasons.length ||
              selectedFilters.substat.length) > 0 && (
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-red-600 hover:text-red-800 underline whitespace-nowrap"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-row flex-wrap gap-2 w-full items-start justify-end">
          {isMultiTeam && (
            <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
              <MultiSelectDropdown
                options={teamFilterOptions}
                options2={[]}
                def="Select Team"
                setSelectedOption={handleDropdownChange}
                selectedOption={selectedFilters}
                filterKey="teams"
                disabled={false}
              />
            </div>
          )}

          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown
              options={seasonFilterOptions}
              options2={[]}
              def="Select Season"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="seasons"
              disabled={false}
            />
          </div>

          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown
              options={Object.keys(playerProfileData?.stat_mapping ?? {}) ?? []}
              options2={[]}
              def="Select Category"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="stat"
              disabled={false}
            />
          </div>

          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown
              options={
                selectedFilters.stat.length > 0
                  ? selectedFilters.stat
                      .map((stat) => playerProfileData?.stat_mapping?.[stat])
                      .flat()
                      .filter((item): item is string => item !== undefined)
                  : []
              }
              options2={[]}
              def="Select Substat"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="substat"
              disabled={!selectedFilters.stat.length}
            />
          </div>
        </div>
      </div>

      {errorMessage && errorStatus >= 500 ? (
        <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
          {errorMessage}{" "}
          <button
            className="underline text-blue-600 hover:text-blue-800"
            onClick={() =>
              handleReportIssue(
                `Failed to fetch player profile (Player ID: ${playerId}, SportCode: ${sportsCode}, HTTP ${errorStatus})`
              )
            }
          >
            Click here to report this issue
          </button>
        </div>
      ) : (
        <>
          <div className="w-full overflow-x-auto rounded-lg">
            <div
              className="ag-theme-alpine"
              style={{ height: "500px", width: "100%" }}
            >
              <AgGridReact
                // FIX: Main data only contains actual stats
                rowData={filteredData}
                // FIX: Total row is pinned to bottom
                pinnedBottomRowData={totalRow ? [totalRow] : []}

                columnDefs={getColDefs()}
                defaultColDef={{
                  width: 120,
                  minWidth: 80,
                  resizable: true,
                  sortable: true,
                }}
                getRowStyle={(params) => {
                  // FIX: Style only the pinned row
                  if (params.node.rowPinned === 'bottom') {
                    return {
                      fontWeight: "bold",
                      backgroundColor: "#e5e7eb",
                      borderTop: "2px solid #ccc"
                    };
                  }
                  return undefined;
                }}
                rowHeight={50}
                theme={themeQuartz}
                loadingOverlayComponent={Spinner}
                loading={isLoading}
                suppressHorizontalScroll={false}
                alwaysShowHorizontalScroll={true}
              />
            </div>
          </div>
        </>
      )}

      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </div>
  );
};


// ==================== RECORDS VIEW ====================
const RecordsView = (props: {
  playerData: PlayerData | null | undefined;
  careerProfiles: CareerTeamProfile[];
}) => {
  const { playerId, sportsCode } = useParams();
  const careerPlayerIds = useCareerPlayerIds();
  const isMultiTeam = careerPlayerIds.length > 1;

  const [currentPage, setCurrentPage, currentPageRef] = useReactiveRef<number>(0);
  const [itemsPerPage, setItemsPerPage, itemsPerPageRef] = useReactiveRef<number>(10);

  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [playerRecordsRes, setPlayerRecordsRes, playerRecordsResRef] = useReactiveRef<PlayerRecordsRes | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{
    seasons: number[];
    substat: string[];
    stat: string[];
    teams: string[];
  }>({ seasons: [], substat: [], stat: [], teams: [] });
  const [isLoading, setisLoading] = useState(false);
  const [currentDuration, setCurrentDuration] = useState<"career" | "season" | "game">("game")

  const formatNumericValue = (value: any): string | number => {
    if (value === "-" || value === null || value === undefined) return '-';
    
    const stringValue = String(value).trim();
    if (stringValue === "-") return '-';
    
    if (typeof value === 'number') {
      if (Number.isInteger(value)) {
        return value;
      }
      return parseFloat(value.toFixed(2));
    }
    
    const parsed = parseFloat(stringValue);
    
    if (!isNaN(parsed) && String(parsed) === stringValue) {
      if (Number.isInteger(parsed)) {
        return parsed;
      }
      return parseFloat(parsed.toFixed(2));
    }
    
    return stringValue;
  };

  function handleDropdownChange(key: any, value: any) {
    if (isLoading) return;
    
    const updatedFilters: any = { ...selectedFilters };
    if (key === "seasons" || key === "substat" || key === "stat" || key === "teams") {
      updatedFilters[key] = updatedFilters[key].includes(value)
        ? updatedFilters[key].filter((item: any) => item !== value)
        : [...updatedFilters[key], value];
    }

    setSelectedFilters(updatedFilters);
    if (!playerRecordsRes?.PlayerRecords.length) return;
  }

  const handleDeleteFilter = (filter: string) => {
    if (isLoading) return;

    if (selectedFilters?.teams?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        teams: selectedFilters.teams.filter((team) => team !== filter),
      });
      setCurrentPage(0);
      return;
    }
    
    if (selectedFilters?.seasons.includes(parseInt(filter))) {
      setSelectedFilters({ 
        ...selectedFilters, 
        seasons: selectedFilters.seasons.filter((season) => season !== parseInt(filter)) 
      });
      setCurrentPage(0);
      return;
    }

    const isStatCategory = selectedFilters?.stat?.includes(filter);
    
    if (isStatCategory) {
      const categoryStats = props?.playerData?.stat_mapping?.[filter] || [];
      const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
      const updatedSubstat = selectedFilters.substat.filter(
        (substat: string) => !categoryStats.includes(substat)
      );
      
      setSelectedFilters({
        ...selectedFilters,
        stat: updatedStat,
        substat: updatedSubstat
      });
      setCurrentPage(0);
      return;
    }

    if (selectedFilters?.substat?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        substat: selectedFilters.substat.filter((s: string) => s !== filter)
      });
      setCurrentPage(0);
      return;
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(Math.max(0, pageNumber - 1));
  }

  const handleItemsPerPageChange = (_itemsPerPage: number) => {
    setItemsPerPage(_itemsPerPage);
    setCurrentPage(0);
  }
  
  const handleClearAllFilters = () => {
    if (isLoading) return;
    setSelectedFilters({ seasons: [], substat: [], stat: [], teams: [] });
  };
  
  const getPlayerRecords = async (duration: "career" | "game" | "season" = "game") => {
    try {
      setisLoading(true);
      setErrorMessage("");
      setErrorStatus(0);

      const profiles =
        props.careerProfiles.length > 0
          ? props.careerProfiles
          : careerPlayerIds.map((id) => ({
              playerId: id,
              PlayerName: props.playerData?.PlayerName || "",
              TeamName: props.playerData?.TeamName || "",
              TeamCode: props.playerData?.TeamCode || "",
              PlayerPosition: props.playerData?.PlayerPosition || "",
              stat_mapping: props.playerData?.stat_mapping || {},
            }));

      const allRecords: any[] = [];
      let totalCount = 0;

      for (const profile of profiles) {
        const payload = {
          player_name: profile.PlayerName || props.playerData?.PlayerName,
          Player_id: profile.playerId,
          SportCode: props.playerData?.SportCode,
          TeamCode: profile.TeamCode || props.playerData?.TeamCode,
          StatPeriod: duration,
        };

        const playerRecords: { data: PlayerRecordsRes } = await api.post(
          PLAYER_RECORDS,
          payload
        );

        const rows = playerRecords.data?.PlayerRecords ?? [];
        totalCount += playerRecords.data?.TotalCount ?? rows.length;

        for (const playerRecord of rows) {
          const formattedRecord: any = {};
          Object.keys(playerRecord).forEach((key) => {
            const value = (playerRecord as any)[key];
            if (value === "-" || value === null || value === undefined) {
              formattedRecord[key] = "-";
            } else {
              formattedRecord[key] = formatNumericValue(value);
            }
          });
          if (isMultiTeam) {
            formattedRecord.TEAM = profile.TeamName || "";
          }
          allRecords.push(formattedRecord);
        }
      }

      setPlayerRecordsRes({
        PlayerRecords: allRecords,
        TotalCount: totalCount || allRecords.length,
      } as PlayerRecordsRes);
    } catch (err: any) {
      console.error("Failed to fetch player records:", err);
      setErrorStatus(err?.response?.status ?? 500);
      setErrorMessage(err?.response?.data?.detail || "Something went wrong while fetching player records.");
    } finally {
      setisLoading(false);
    }
  };

  const getFilteredRowData = () => {
    return ((playerRecordsRes?.PlayerRecords ?? []) as any)
      .filter((playerRecord: any) => {
        if (
          isMultiTeam &&
          selectedFilters.teams.length > 0 &&
          !selectedFilters.teams.includes(playerRecord.TEAM)
        ) {
          return false;
        }
        return selectedFilters.seasons.length > 0
          ? selectedFilters.seasons.includes(playerRecord.season)
          : true;
      })
      .filter((playerRecord: any) => {
        if (!selectedFilters.stat.length) return true;
        const allStats: string[] = selectedFilters.stat.map((stat: string) => {
          const categoryStats: string[] | undefined = props?.playerData?.stat_mapping?.[stat];
          return categoryStats ?? [];
        }).flat();
        return allStats.includes(playerRecord.stat);
      })
      .filter((playerHigh: any) => {
        if (!selectedFilters.substat.length) return true;
        return selectedFilters.substat.includes(playerHigh.stat)
      });
  }

  const getColumnDefs = () => {
    const allColumns = [
      { field: "season", sortable: true },
      ...(isMultiTeam ? [{ field: "TEAM", headerName: "Team", sortable: true }] : []),
      { field: "stat", headerName: "Stat Names", sortable: true },
      { field: "value", headerName: "Value", sortable: true },
      { field: "rank", headerName: "Rank", sortable: true },
      { 
        field: "next_rank_value", 
        headerName: "Next Rank Value",
        cellDataType: 'text',
        sortable: true
      },
      { field: "game_date", headerName: "Game Date", sortable: true },
      { field: "opponent_team_name", headerName: "Opponent Team Name", sortable: true },
    ];

    if (currentDuration === "career") {
      return allColumns.filter(col => 
        !["season", "game_date", "opponent_team_name"].includes(col.field)
      );
    } else if (currentDuration === "season") {
      return allColumns.filter(col => 
        !["game_date", "opponent_team_name"].includes(col.field)
      );
    }
    
    return allColumns;
  };

  useEffect(() => {
    if (!props.playerData?.SportCode) return;
    if (careerPlayerIds.length > 1 && props.careerProfiles.length === 0) return;
    getPlayerRecords();
  }, [props.playerData?.SportCode, props.careerProfiles, careerPlayerIds.join(",")]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedFilters]);

  const reportIssue = useReportIssue();

  const handleReportIssue = (errorContext?: string) => {
    console.log('Report issue clicked!');
    reportIssue.openModal({
      ComponentSource: 'PlayerDashboard - Record View',
      ErrorContext: errorContext || errorMessage || '',
      UserContext: {
        playerId,
        sportsCode,
        errorStatus,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });
  };
  
  return (
    <>
      <div className="flex flex-col gap-3 w-full mb-2">
        <div className="flex flex-row flex-wrap items-center gap-2 w-full">
          <div className="text-sm font-medium shrink-0">Filters:-</div>
          <div className="flex gap-2 flex-wrap items-center flex-1 min-w-0">
            {[
              ...selectedFilters.teams,
              ...selectedFilters.stat,
              ...selectedFilters.seasons,
              ...selectedFilters.substat,
            ].map(
              (filter, index) => (
                <Pill
                  key={index}
                  text={String(filter)}
                  showCross={true}
                  onClick={() => handleDeleteFilter(String(filter))}
                />
              )
            )}
            {(selectedFilters.teams.length ||
              selectedFilters.stat.length ||
              selectedFilters.seasons.length ||
              selectedFilters.substat.length) > 0 && (
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-red-600 hover:text-red-800 underline whitespace-nowrap"
                disabled={isLoading}
              >
                Clear All
              </button>
            )}
          </div>
        </div>
        
        <div className="flex flex-row flex-wrap gap-2 w-full items-start justify-end">
          {isMultiTeam && (
            <div className={`min-w-[10rem] w-[12rem] max-w-[14rem] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
              <MultiSelectDropdown
                options={[
                  ...new Set([
                    ...props.careerProfiles.map((p) => p.TeamName).filter(Boolean),
                    ...((playerRecordsRes?.PlayerRecords ?? []) as any[])
                      .map((r) => r.TEAM)
                      .filter(Boolean),
                  ]),
                ]}
                options2={[]}
                def="Select Team"
                setSelectedOption={handleDropdownChange}
                selectedOption={selectedFilters}
                filterKey="teams"
                disabled={false}
              />
            </div>
          )}

          <div className={`min-w-[10rem] w-[12rem] max-w-[14rem] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <DropdownSelect
              options={["season", "career", "game"]}
              options2={[]}
              def="Select duration"
              setSelectedOption={setCurrentDuration}
              selectedOption={currentDuration}
              onChange={(duration: "season" | "career" | "game") => {
                if (!isLoading) {
                  getPlayerRecords(duration);
                }
              }}
            />
          </div>
          
          {currentDuration !== "career" && (
            <div className={`min-w-[10rem] w-[12rem] max-w-[14rem] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
              <MultiSelectDropdown
                options={Array.from(
                  new Set(
                    ((playerRecordsRes?.PlayerRecords ?? []) as any[])
                      .filter(
                        (r) =>
                          !isMultiTeam ||
                          selectedFilters.teams.length === 0 ||
                          selectedFilters.teams.includes(r.TEAM)
                      )
                      .map((playerHigh) => playerHigh.season)
                  )
                )}
                options2={[]}
                def="Select Season"
                setSelectedOption={handleDropdownChange}
                selectedOption={selectedFilters}
                filterKey="seasons"
                disabled={false}
              />
            </div>
          )}
          
          <div className={`min-w-[10rem] w-[12rem] max-w-[14rem] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <MultiSelectDropdown
              options={Object.keys(props?.playerData?.stat_mapping ?? {}) ?? []}
              options2={[]}
              def="Select Category"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="stat"
              disabled={false}
            />
          </div>
          
          <div className={`min-w-[10rem] w-[12rem] max-w-[14rem] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
            <MultiSelectDropdown
              options={
                selectedFilters.stat.map((stat) => props?.playerData?.stat_mapping?.[stat]).flat()
              }
              options2={[]}
              def="Select Substat"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="substat"
              disabled={!selectedFilters.stat}
            />
          </div>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-lg">
        <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
        {errorMessage && errorStatus >= 500 ? (
          <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
            {errorMessage}{" "}
            <button
              className="underline text-blue-600 hover:text-blue-800"
              onClick={() =>
                handleReportIssue(
                  `Failed to fetch player records (Player ID: ${playerId}, SportCode: ${sportsCode}, HTTP ${errorStatus})`
                )
              }
            >
              Click here to report this issue
            </button>
          </div>
        ) : (
          <>
            <AgGridReact
              rowData={getFilteredRowData()}
              columnDefs={getColumnDefs()}
              defaultColDef={{
                flex: 1,
                sortable: true,
                valueFormatter: (params) => {
                  const val = params.value;
                  return val === null || val === undefined ? "-" : val;
                },
                comparator: (valueA, valueB) => {
                  const isADash = valueA === "-" || valueA === null || valueA === undefined;
                  const isBDash = valueB === "-" || valueB === null || valueB === undefined;
                  
                  if (isADash && isBDash) return 0;
                  if (isADash) return 1;
                  if (isBDash) return -1;
                  
                  const isNumericString = (val: any) => /^-?\d*\.?\d+$/.test(String(val));

                  if (isNumericString(valueA) && isNumericString(valueB)) {
                    return parseFloat(String(valueA)) - parseFloat(String(valueB));
                  }
                  
                  return String(valueA).localeCompare(String(valueB));
                },
              }}
              pagination={true}
              paginationPageSize={itemsPerPage}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              theme={themeQuartz}
              loadingOverlayComponent={Spinner}
              loading={isLoading}
            />
          </>
        )}
        </div>
      </div>

      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </>
  )
}
// ==================== SEASON AND HIGHS VIEW ====================
// ==================== SEASON AND HIGHS VIEW ====================
const SeasonAndHighsView = (props: {
  playerData: PlayerData | null | undefined;
  careerProfiles: CareerTeamProfile[];
}) => {
  const { playerId, sportsCode } = useParams();
  const careerPlayerIds = useCareerPlayerIds();
  const isMultiTeam = careerPlayerIds.length > 1;
  const [currentPage, setCurrentPage, currentPageRef] = useReactiveRef<number>(0);
  const [itemsPerPage, setItemsPerPage, itemsPerPageRef] = useReactiveRef<number>(10);

  const [playerHighsRes, setPlayerHighsRes, playerHighsResRef] = useReactiveRef<PlayerHighsRes | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{
    seasons: number[];
    substat: string[];
    stat: string[];
    teams: string[];
  }>({ seasons: [], substat: [], stat: [], teams: [] });
  const [currentDuration, setCurrentDuration] = useState<"career" | "season">("career")
  const [isLoading, setisLoading] = useState(false);
  
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const formatNumericValue = (value: any): string | number => {
    if (value === null || value === undefined) return '-';
    if (typeof value !== 'number') return value;
    
    if (Number.isInteger(value)) {
      return value;
    }
    
    return parseFloat(value.toFixed(2));
  };

  function handleDropdownChange(key: any, value: any) {
    const updatedFilters: any = { ...selectedFilters };
    if (key === "seasons" || key === "substat" || key === "stat" || key === "teams") {
      updatedFilters[key] = updatedFilters[key].includes(value)
        ? updatedFilters[key].filter((item: any) => item !== value)
        : [...updatedFilters[key], value];
    }

    setSelectedFilters(updatedFilters);
    if (!playerHighsRes?.PlayerHighs.length) return;
  }

  const handleDeleteFilter = (filter: string) => {
    if (selectedFilters?.teams?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        teams: selectedFilters.teams.filter((team) => team !== filter),
      });
      setCurrentPage(0);
      return;
    }

    // Handle both numeric and string season values (e.g., 2023 or "Current")
    const numericFilter = parseInt(filter);
    const seasonMatch = selectedFilters?.seasons.some((season: any) =>
      String(season) === String(filter) || ( !isNaN(numericFilter) && season === numericFilter )
    );

    if (seasonMatch) {
      setSelectedFilters({
        ...selectedFilters,
        seasons: selectedFilters.seasons.filter((season: any) => String(season) !== String(filter))
      });
      setCurrentPage(0);
      return;
    }

    const isStatCategory = selectedFilters?.stat?.includes(filter);
    
    if (isStatCategory) {
      const categoryStats = props?.playerData?.stat_mapping?.[filter] || [];
      const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
      const updatedSubstat = selectedFilters.substat.filter(
        (substat: string) => !categoryStats.includes(substat)
      );
      
      setSelectedFilters({
        ...selectedFilters,
        stat: updatedStat,
        substat: updatedSubstat
      });
      setCurrentPage(0);
      return;
    }

    if (selectedFilters?.substat?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        substat: selectedFilters.substat.filter((s: string) => s !== filter)
      });
      setCurrentPage(0);
      return;
    }
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(Math.max(0, pageNumber - 1));
  }

  const handleItemsPerPageChange = (_itemsPerPage: number) => {
    setItemsPerPage(_itemsPerPage);
    setCurrentPage(0);
  };

  const getPlayerHighs = async (duration: "season" | "career" = "career") => {
    try {
      setisLoading(true);

      const profiles =
        props.careerProfiles.length > 0
          ? props.careerProfiles
          : careerPlayerIds.map((id) => ({
              playerId: id,
              PlayerName: props.playerData?.PlayerName || "",
              TeamName: props.playerData?.TeamName || "",
              TeamCode: props.playerData?.TeamCode || "",
              PlayerPosition: props.playerData?.PlayerPosition || "",
              stat_mapping: props.playerData?.stat_mapping || {},
            }));

      const allHighs: any[] = [];
      let totalCount = 0;

      for (const profile of profiles) {
        const payload = {
          Player_id: profile.playerId,
          SportCode: props.playerData?.SportCode,
          StatPeriod: duration,
          Limit: itemsPerPageRef.current,
          Offset: currentPageRef.current * itemsPerPageRef.current,
        };
        const playerHighsRes: { data: PlayerHighsRes } = await api.post(
          PLAYER_SEASON_AND_CAREER_HIGHS,
          payload
        );

        const rows = playerHighsRes.data?.PlayerHighs ?? [];
        totalCount += (playerHighsRes.data as any)?.TotalCount ?? rows.length;

        for (const playerHigh of rows) {
          const formattedHigh: any = {};
          Object.keys(playerHigh).forEach((key) => {
            formattedHigh[key] = formatNumericValue((playerHigh as any)[key]);
          });
          if (isMultiTeam) {
            formattedHigh.TEAM = profile.TeamName || "";
          }
          allHighs.push(formattedHigh);
        }
      }

      setPlayerHighsRes({
        PlayerHighs: allHighs,
        TotalCount: totalCount || allHighs.length,
      } as PlayerHighsRes);

    } catch (err: any) { 
      console.log("Error msg in season and player highs")
      setErrorStatus(err?.response?.status ?? 500);
      setErrorMessage(err?.response?.data?.detail || "Something went wrong while fetching player records.");
    }
    finally {
      setisLoading(false);
    }
  }

  const handleClearAllFilters = () => {
    setSelectedFilters({ seasons: [], substat: [], stat: [], teams: [] });
  };

  const getFilteredRowData = (): any[] => {
    return ((playerHighsRes?.PlayerHighs ?? []) as any)
      .filter((playerHigh: any) => {
        if (
          isMultiTeam &&
          selectedFilters.teams.length > 0 &&
          !selectedFilters.teams.includes(playerHigh.TEAM)
        ) {
          return false;
        }
        if (selectedFilters.seasons.length === 0) return true;
        // Handle both numeric and string season values
        return selectedFilters.seasons.some((season: any) =>
          String(season) === String(playerHigh.season) ||
          season === playerHigh.season
        );
      })
      .filter((playerHigh: any) => {
        if (!selectedFilters.stat.length) return true;
        const allStats: string[] = selectedFilters.stat.map((stat: string) => {
          const categoryStats: string[] | undefined = props?.playerData?.stat_mapping?.[stat];
          return categoryStats ?? [];
        }).flat();
        return allStats.includes(playerHigh.stat);
      })
      .filter((playerHigh: any) => {
        if (!selectedFilters.substat.length) return true;
        return selectedFilters.substat.includes(playerHigh.stat)
      });
  }

  const getColumnDefs = () => {
    const allColumns = [
      { field: "season", sortable: true },
      ...(isMultiTeam ? [{ field: "TEAM", headerName: "Team", sortable: true }] : []),
      { field: "stat", headerName: "Stat Names", sortable: true },
      { field: "value", headerName: "Value", sortable: true },
      { field: "game_date", headerName: "Game Date", sortable: true },
      { field: "opponent_team_name", headerName: "Opponent Team Name", sortable: true },
    ];

    if (currentDuration === "career") {
      return allColumns.filter(col => col.field !== "season");
    }
    // For season view, show all columns including game_date and opponent_team_name
    return allColumns;
  };

  useEffect(() => {
    if (!props.playerData?.SportCode) return;
    if (careerPlayerIds.length > 1 && props.careerProfiles.length === 0) return;
    getPlayerHighs();
  }, [props.playerData?.SportCode, props.careerProfiles, careerPlayerIds.join(",")]);

  useEffect(() => {
    console.warn("setting page to 1");
    setCurrentPage(0);
  }, [selectedFilters]);

  const reportIssue = useReportIssue();

  const handleReportIssue = (errorContext?: string) => {
    console.log('Report issue clicked!');
    reportIssue.openModal({
      ComponentSource: 'PlayerDashboard - Season And Highs View',
      ErrorContext: errorContext || errorMessage || '',
      UserContext: {
        playerId,
        sportsCode,
        errorStatus,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });
  };

  return (
    <>
      <div className="flex flex-col gap-3 w-full mb-2">
        <div className="flex flex-row flex-wrap items-center gap-2 w-full">
          <div className="text-sm font-medium shrink-0">Filters:-</div>
          <div className="flex gap-2 flex-wrap items-center flex-1 min-w-0">
            {([
              ...selectedFilters.teams,
              ...selectedFilters.stat,
              ...selectedFilters.seasons,
              ...selectedFilters.substat,
            ])
              .map((filter, index) => (
                <Pill
                  key={index}
                  text={String(filter)}
                  showCross={true}
                  onClick={() => handleDeleteFilter(String(filter))}
                />
              ))}
            {(selectedFilters.teams.length ||
              selectedFilters.stat.length ||
              selectedFilters.seasons.length ||
              selectedFilters.substat.length) > 0 && (
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-red-600 hover:text-red-800 underline whitespace-nowrap"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
        
        <div className="flex flex-row flex-wrap gap-2 w-full items-start justify-end">
          {isMultiTeam && (
            <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
              <MultiSelectDropdown
                options={[
                  ...new Set([
                    ...props.careerProfiles.map((p) => p.TeamName).filter(Boolean),
                    ...((playerHighsRes?.PlayerHighs ?? []) as any[])
                      .map((r) => r.TEAM)
                      .filter(Boolean),
                  ]),
                ]}
                options2={[]}
                def="Select Team"
                setSelectedOption={handleDropdownChange}
                selectedOption={selectedFilters}
                filterKey="teams"
                disabled={false}
              />
            </div>
          )}
          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <DropdownSelect
              options={["season", "career"]}
              options2={[]}
              def="Select duration"
              setSelectedOption={setCurrentDuration}
              selectedOption={currentDuration}
              onChange={(duration: "season" | "career") => getPlayerHighs(duration)}
            />
          </div>
          {currentDuration === "season" && (
            <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
              <MultiSelectDropdown
                options={Array.from(
                  new Set(
                    ((playerHighsRes?.PlayerHighs ?? []) as any[])
                      .filter(
                        (r) =>
                          !isMultiTeam ||
                          selectedFilters.teams.length === 0 ||
                          selectedFilters.teams.includes(r.TEAM)
                      )
                      .map((playerHigh) => playerHigh.season)
                  )
                )}
                options2={[]}
                def="Select Season"
                setSelectedOption={handleDropdownChange}
                selectedOption={selectedFilters}
                filterKey="seasons"
                disabled={false}
              />
            </div>
          )}
          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown
              options={Object.keys(props?.playerData?.stat_mapping ?? {}) ?? []}
              options2={[]}
              def="Select Category"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="stat"
              disabled={false}
            />
          </div>
          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown
              options={selectedFilters.stat.map((stat) => props?.playerData?.stat_mapping?.[stat]).flat()}
              options2={[]}
              def="Select Substat"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="substat"
              disabled={!selectedFilters.stat}
            />
          </div>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-lg">
        <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
        {errorMessage && errorStatus >= 500 ? (
          <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
            {errorMessage}{" "}
            <button
              className="underline text-blue-600 hover:text-blue-800"
              onClick={() =>
                handleReportIssue(
                  `Failed to fetch player profile (Player ID: ${playerId}, SportCode: ${sportsCode}, HTTP ${errorStatus})`
                )
              }
            >
              Click here to report this issue
            </button>
          </div>
        ) : (
          <>
            <AgGridReact
              rowData={getFilteredRowData()}
              columnDefs={getColumnDefs()}
              defaultColDef={{
                flex: 1,
                sortable: true,
                valueFormatter: (params) => {
                  const val = params.value;
                  return val === null || val === undefined ? "-" : val;
                },
                comparator: (valueA, valueB) => {
                  const isADash = valueA === "-" || valueA === null || valueA === undefined;
                  const isBDash = valueB === "-" || valueB === null || valueB === undefined;
                  
                  if (isADash && isBDash) return 0;
                  if (isADash) return 1;
                  if (isBDash) return -1;
                  
                  const isNumericString = (val: any) => /^-?\d*\.?\d+$/.test(String(val));

                  if (isNumericString(valueA) && isNumericString(valueB)) {
                    return parseFloat(String(valueA)) - parseFloat(String(valueB));
                  }
                  
                  return String(valueA).localeCompare(String(valueB));
                },
              }}
              pagination={true}
              paginationPageSize={itemsPerPage}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              theme={themeQuartz}
              loadingOverlayComponent={Spinner}
              loading={isLoading}
            />
          </>
        )}
        </div>
      </div>

      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </>
  );
}

const GameLogsView = (props: {
  playerData: PlayerData | null | undefined;
  careerProfiles: CareerTeamProfile[];
}) => {
  const [selectedFilters, setSelectedFilters] = useState<IFilters>({ seasons: [], substat: [], stat: [], teams: [] });
  const [gameLogsRes, setGameLogsRes] = useState<GameLogsRes | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [isLoading, setisLoading] = useState(false);

  const { playerId, sportsCode } = useParams();
  const careerPlayerIds = useCareerPlayerIds();
  const isMultiTeam = careerPlayerIds.length > 1;

  const formatNumericValue = (value: any): string | number => {
    if (value === null || value === undefined) return '-';
    if (typeof value !== 'number') return value;
    
    if (Number.isInteger(value)) {
      return value;
    }
    
    return parseFloat(value.toFixed(2));
  };

  function handleDropdownChange(key: any, value: any) {
    const updatedFilters: any = { ...selectedFilters };
    if (key === "seasons" || key === "substat" || key === "stat" || key === "teams") {
      updatedFilters[key] = updatedFilters[key].includes(value)
        ? updatedFilters[key].filter((item: any) => item !== value)
        : [...updatedFilters[key], value];
    }

    setSelectedFilters(updatedFilters);
    if (!gameLogsRes) return;
  }

  const filteredData = useMemo(() => {
    const filtered = (gameLogsRes?.stats ?? []).filter((stat: any) => {
      const teamOk =
        !isMultiTeam ||
        selectedFilters.teams.length === 0 ||
        selectedFilters.teams.includes(stat.TEAM);
      const seasonOk =
        selectedFilters.seasons.length === 0 ||
        selectedFilters.seasons.includes(String(stat.season));
      return teamOk && seasonOk;
    });
    
    return filtered.map((stat: any) => {
      const formattedStat: any = {};
      Object.keys(stat).forEach(key => {
        formattedStat[key] = formatNumericValue(stat[key]);
      });
      return formattedStat;
    });
  }, [gameLogsRes, selectedFilters.seasons, selectedFilters.teams, isMultiTeam]);

  const seasonFilterOptions = useMemo(() => {
    const rows = (gameLogsRes?.stats ?? []).filter((stat: any) =>
      !isMultiTeam ||
      selectedFilters.teams.length === 0 ||
      selectedFilters.teams.includes(stat.TEAM)
    );
    const fromStats = rows
      .map((stat: any) => stat.season)
      .filter((s: unknown) => s !== null && s !== undefined && s !== "");
    const fromApi = (gameLogsRes?.Seasons ?? []).filter(
      (s) => s !== null && s !== undefined && s !== ""
    );
    const keys = new Set([...fromStats, ...fromApi].map((s) => String(s)));
    return [...keys].sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) return nb - na;
      return b.localeCompare(a);
    });
  }, [gameLogsRes, selectedFilters.teams, isMultiTeam]);

  const handleDeleteFilter = (filter: string) => {
    if (selectedFilters?.teams?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        teams: selectedFilters.teams.filter((team) => team !== filter),
      });
      return;
    }

    if (selectedFilters?.seasons?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        seasons: selectedFilters.seasons.filter((season) => season !== filter)
      });
      return;
    }

    const isStatCategory = selectedFilters?.stat?.includes(filter);
    
    if (isStatCategory) {
      const categoryStats = gameLogsRes?.stat_mapping?.[filter] || [];
      const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
      const updatedSubstat = selectedFilters.substat.filter(
        (substat: string) => !categoryStats.includes(substat)
      );
      
      setSelectedFilters({
        ...selectedFilters,
        stat: updatedStat,
        substat: updatedSubstat
      });
      return;
    }

    if (selectedFilters?.substat?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        substat: selectedFilters.substat.filter((s: string) => s !== filter)
      });
      return;
    }
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({ seasons: [], substat: [], stat: [], teams: [] });
  };

  const calculateTotals = () => {
    const filteredStats = (gameLogsRes?.stats ?? [])
      .filter((stat: any) => {
        const teamOk =
          !isMultiTeam ||
          selectedFilters.teams.length === 0 ||
          selectedFilters.teams.includes(stat.TEAM);
        const seasonOk =
          selectedFilters.seasons.length === 0 ||
          selectedFilters.seasons.includes(String(stat.season));
        return teamOk && seasonOk;
      });
    
    if (filteredStats.length === 0) return {
      season: "TOTAL",
      game_date: "-", 
      opponent_team_name: "-",
      ...(isMultiTeam ? { TEAM: "-" } : {}),
    };
    
    const totals: any = {
      season: "TOTAL",
      game_date: "-",
      opponent_team_name: "-",
      ...(isMultiTeam ? { TEAM: "-" } : {}),
    };
    
    Object.keys(filteredStats[0] || {}).forEach(key => {
      if (!["season", "game_date", "opponent_team_name", "TEAM"].includes(key)) {
        const isPercentageStat = key.toLowerCase().includes('%');
        
        if (isPercentageStat) {
          const sum = filteredStats.reduce((acc, stat) => {
            const value = stat[key];
            return acc + (typeof value === 'number' && !isNaN(value) ? value : 0);
          }, 0);
          const count = filteredStats.filter(stat => {
            const value = stat[key];
            return typeof value === 'number' && !isNaN(value);
          }).length;
          const average = count > 0 ? (sum / count) : 0;
          totals[key] = formatNumericValue(average);
        } else {
          const sum = filteredStats.reduce((acc, stat) => {
            const value = stat[key];
            return acc + (typeof value === 'number' && !isNaN(value) ? value : 0);
          }, 0);
          totals[key] = formatNumericValue(sum);
        }
      }
    });
    
    return totals;
  };

  const getTotalsRowData = () => {
    return [calculateTotals()];
  };

  const getColDefs = () => {
    const totals = calculateTotals();
    
    const fixedColumns = [
      { 
        field: "season", 
        label: "SEASON", 
        headerName: "Season", 
        pinned: 'left' as const,
        width: 100,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params.node.rowPinned === 'top') {
            return "TOTAL";
          }
          const val = params.value;
          return val === null || val === undefined ? '-' : val;
        }
      },
      ...(isMultiTeam
        ? [
            {
              field: "TEAM",
              headerName: "Team",
              pinned: "left" as const,
              width: 140,
              sortable: true,
              valueFormatter: (params: any) => {
                if (params.node.rowPinned === "top") return "-";
                const val = params.value;
                return val === null || val === undefined ? "-" : val;
              },
            },
          ]
        : []),
      { 
        field: "game_date", 
        headerName: "Game Date", 
        pinned: 'left' as const,
        width: 120,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params.node.rowPinned === 'top') {
            return "-";
          }
          const val = params.value;
          return val === null || val === undefined ? '-' : val;
        }
      },
      { 
        field: "opponent_team_name", 
        headerName: "Opponent Team Name", 
        pinned: 'left' as const,
        width: 180,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params.node.rowPinned === 'top') {
            return "-";
          }
          const val = params.value;
          return val === null || val === undefined ? '-' : val;
        }
      }
    ];

    const statColumns = Object.keys(gameLogsRes?.stats[0] ?? {})
      .filter((key) => !["season", "game_date", "opponent_team_name", "TEAM"].includes(key))
      .filter((key) => {
        if (!selectedFilters.stat.length) return true;
        const allStats: string[] = selectedFilters.stat.map((stat: string) => {
          const categoryStats: string[] | undefined = gameLogsRes?.stat_mapping?.[stat];
          return categoryStats ?? [];
        }).flat();

        return allStats.includes(key);
      })
      .filter((key) => {
        if (!selectedFilters.substat.length) return true;
        return selectedFilters.substat.includes(key)
      })
      .filter((key) => {
        const totalValue = totals[key];
        const numericTotal = typeof totalValue === 'number' ? totalValue : parseFloat(String(totalValue));
        return !isNaN(numericTotal) && numericTotal !== 0;
      })
      .sort((a, b) => {
        const valA = Number(totals[a]) || 0;
        const valB = Number(totals[b]) || 0;
        return valB - valA;
      })
      .map((key) => ({
        field: key,
        headerName: key,
        sortable: true,
        resizable: true,
        width: 120,
        valueFormatter: (params: any) => {
          const val = params.value;
          if (val === null || val === undefined) return '-';
          return val;
        }
      }));

    return [...fixedColumns, ...statColumns];
  }

  const getGameLogs = async (season?: number | null) => {
    try {
      setisLoading(true);

      const profiles =
        props.careerProfiles.length > 0
          ? props.careerProfiles
          : careerPlayerIds.map((id) => ({
              playerId: id,
              PlayerName: props.playerData?.PlayerName || "",
              TeamName: props.playerData?.TeamName || "",
              TeamCode: props.playerData?.TeamCode || "",
              PlayerPosition: props.playerData?.PlayerPosition || "",
              stat_mapping: props.playerData?.stat_mapping || {},
            }));

      const allStats: any[] = [];
      let mergedMapping: Record<string, any> = {};
      let primary: GameLogsRes | null = null;
      const mergedSeasonKeys = new Set<string>();

      const addSeasonKey = (value: unknown) => {
        if (value === null || value === undefined || value === "") return;
        mergedSeasonKeys.add(String(value));
      };

      const sortSeasonKeysDesc = (keys: string[]) =>
        [...keys].sort((a, b) => {
          const na = Number(a);
          const nb = Number(b);
          if (!Number.isNaN(na) && !Number.isNaN(nb)) return nb - na;
          return b.localeCompare(a);
        });

      for (const profile of profiles) {
        const payload: any = {
          Player_id: profile.playerId,
          SportCode: props.playerData?.SportCode,
          Season: season || null,
        };

        const res: { data: GameLogsRes } = await api.post(PLAYER_GAME_LOGS, payload);
        if (!primary) {
          primary = res.data;
        }
        if (res.data?.stat_mapping && typeof res.data.stat_mapping === "object") {
          mergedMapping = { ...mergedMapping, ...res.data.stat_mapping };
        }
        (res.data?.Seasons ?? []).forEach(addSeasonKey);
        const rows = Array.isArray(res.data?.stats) ? res.data.stats : [];
        for (const row of rows) {
          addSeasonKey(row.season);
          allStats.push({
            ...row,
            ...(isMultiTeam ? { TEAM: profile.TeamName || "" } : {}),
          });
        }
      }

      const mergedSeasons = sortSeasonKeysDesc([...mergedSeasonKeys]).map((key) => {
        const n = Number(key);
        return Number.isNaN(n) ? key : n;
      });

      setGameLogsRes({
        ...(primary as GameLogsRes),
        stats: allStats,
        Seasons: mergedSeasons as GameLogsRes["Seasons"],
        stat_mapping:
          Object.keys(mergedMapping).length > 0
            ? mergedMapping
            : primary?.stat_mapping,
      } as GameLogsRes);

    } catch (err: any) {
      console.error("Failed to fetch player records:", err);
      setErrorStatus(err?.response?.status ?? 500);
      setErrorMessage(err?.response?.data?.detail || "Something went wrong while fetching player records.");
    }
    finally {
      setisLoading(false);
    }
  }

  useEffect(() => {
    if (!props.playerData?.SportCode) return;
    if (careerPlayerIds.length > 1 && props.careerProfiles.length === 0) return;
    getGameLogs();
  }, [props.playerData?.SportCode, props.careerProfiles, careerPlayerIds.join(",")]);

  const reportIssue = useReportIssue();

  const handleReportIssue = (errorContext?: string) => {
    console.log('Report issue clicked!');
    reportIssue.openModal({
      ComponentSource: 'PlayerDashboard - GameLogsView',
      ErrorContext: errorContext || errorMessage || '',
      UserContext: {
        playerId,
        sportsCode,
        errorStatus,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });
  };

  const handleItemsPerPageChange = (_itemsPerPage: number) => {
    setItemsPerPage(_itemsPerPage);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-3 w-full">
        <div className="flex flex-row flex-wrap items-center gap-2 w-full">
          <div className="text-sm font-medium shrink-0">Filters:-</div>
          <div className="flex gap-2 flex-wrap items-center flex-1 min-w-0">
            {([
              ...selectedFilters.teams,
              ...selectedFilters.stat,
              ...selectedFilters.seasons,
              ...selectedFilters.substat,
            ])
              .filter((f) => f !== null)
              .map((filter, index) => (
                <Pill key={index} text={filter} showCross={true} onClick={() => handleDeleteFilter(filter)} />
              ))}
            {(selectedFilters.teams.length ||
              selectedFilters.stat.length ||
              selectedFilters.seasons.length ||
              selectedFilters.substat.length) > 0 && (
              <button
                onClick={handleClearAllFilters}
                className="text-xs text-red-600 hover:text-red-800 underline whitespace-nowrap"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-row flex-wrap gap-2 w-full items-start justify-end">
          {isMultiTeam && (
            <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
              <MultiSelectDropdown
                options={[
                  ...new Set([
                    ...props.careerProfiles.map((p) => p.TeamName).filter(Boolean),
                    ...((gameLogsRes?.stats ?? []) as any[])
                      .map((r) => r.TEAM)
                      .filter(Boolean),
                  ]),
                ]}
                options2={[]}
                def="Select Team"
                setSelectedOption={handleDropdownChange}
                selectedOption={selectedFilters}
                filterKey="teams"
                disabled={false}
              />
            </div>
          )}
          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown 
              options={seasonFilterOptions} 
              options2={[]} 
              def="Select Seasons" 
              setSelectedOption={handleDropdownChange} 
              selectedOption={selectedFilters} 
              filterKey="seasons" 
              disabled={false} 
            />
          </div>
          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown 
              options={Object.keys(gameLogsRes?.stat_mapping ?? {}) ?? []} 
              options2={[]} 
              def="Select Category" 
              setSelectedOption={handleDropdownChange} 
              selectedOption={selectedFilters} 
              filterKey="stat" 
              disabled={false} 
            />
          </div>
          <div className="min-w-[10rem] w-[12rem] max-w-[14rem]">
            <MultiSelectDropdown 
              options={selectedFilters.stat.map((stat) => gameLogsRes?.stat_mapping?.[stat]).flat()} 
              options2={[]} 
              def="Select Substat" 
              setSelectedOption={handleDropdownChange} 
              selectedOption={selectedFilters} 
              filterKey="substat" 
              disabled={!selectedFilters.stat} 
            />
          </div>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-lg">
        <div className="ag-theme-alpine" style={{ height: "500px", width: "100%" }}>
        {errorMessage && errorStatus >= 500 ? (
          <div className="bg-red-50 text-red-700 p-4 rounded text-sm mb-4">
            {errorMessage}{" "}
            <button
              className="underline text-blue-600 hover:text-blue-800"
              onClick={() =>
                handleReportIssue(
                  `Failed to fetch player profile (Player ID: ${playerId}, SportCode: ${sportsCode}, HTTP ${errorStatus})`
                )
              }
            >
              Click here to report this issue
            </button>
          </div>
        ) : (
          <>
            <AgGridReact
              rowData={filteredData}
              pinnedTopRowData={getTotalsRowData()}
              columnDefs={getColDefs()}
              defaultColDef={{ 
                width: 120,
                minWidth: 80,
                resizable: true,
                sortable: true,
                valueFormatter: (params) => {
                  const val = params.value;
                  return val === null || val === undefined ? '-' : val;
                },
                comparator: (valueA, valueB) => {
                  const isADash = valueA === "-" || valueA === null || valueA === undefined;
                  const isBDash = valueB === "-" || valueB === null || valueB === undefined;
                  
                  if (isADash && isBDash) return 0;
                  if (isADash) return 1;
                  if (isBDash) return -1;
                  
                  // STRICT CHECK: Only parse as number if string is purely numeric (no hyphens)
                  // This prevents dates like "2016-10-29" from being parsed as "2016"
                  const isNumericString = (val: any) => /^-?\d*\.?\d+$/.test(String(val));

                  if (isNumericString(valueA) && isNumericString(valueB)) {
                    return parseFloat(String(valueA)) - parseFloat(String(valueB));
                  }
                  
                  return String(valueA).localeCompare(String(valueB));
                },
              }}
              getRowStyle={(params) => {
                if (params.node.rowPinned === 'top') {
                  return { 
                    backgroundColor: '#f3f4f6', 
                    fontWeight: 'bold',
                    borderBottom: '2px solid #d1d5db'
                  };
                }
                return {};
              }}
              enableRangeSelection={true}
              animateRows={true}
              pagination={true}
              paginationPageSize={itemsPerPage}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              onPaginationChanged={(params) => {
                const newPageSize = params.api.paginationGetPageSize();
                if (newPageSize !== itemsPerPage) {
                  setItemsPerPage(newPageSize);
                }
              }}
              theme={themeQuartz}
              loadingOverlayComponent={Spinner}
              loading={isLoading}
              suppressHorizontalScroll={false}
              alwaysShowHorizontalScroll={true}
            />
          </>
        )}
        </div>
      </div>
      
      <ReportIssueModal
        isOpen={reportIssue.isModalOpen}
        onClose={reportIssue.closeModal}
        onSubmit={reportIssue.handleSubmit}
        issue={reportIssue.issue}
        setIssue={reportIssue.setIssue}
        feedbackMessage={reportIssue.feedbackMessage}
        feedbackType={reportIssue.feedbackType}
        isLoading={reportIssue.isLoading}
      />
    </div>
    );
}
interface PlayerStreaksRes {
  PlayerStreaks: {
    [key: string]: any; // Changed to flexible structure since backend now returns dynamic keys
  }[];
  TotalCount: number;
}

interface PlayerStreaksMappingRes {
  [key: string]: string[],
}

const StreaksView = (props: {
  playerData: PlayerData | null | undefined;
  careerProfiles: CareerTeamProfile[];
}) => {
  const [searchString, setSearchString] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const { selectedSport, user, team, openToast } = useContext(AuthContext);

  const [playerStreaksRes, setPlayerStreaksRes] = useState<PlayerStreaksRes | null>(null);
  const [playerStreaksMappingRes, setPlayerStreaksMappingRes] = useState<PlayerStreaksMappingRes | null>(null);

  const [data, setData] = useState<any>(null);
  const dashboard_Service = new dashboardServices();
  const [loading, setLoading] = useState<boolean>(false);
  const [filter_config, setfilter_config] = useState<any>(null);

  const [selectedFilters, setSelectedFilters] = useState<any>({ teams: [] });
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isDisabled, setIsDisabled] = useState(false);

  const [streakLength, setStreakLength, streakLengthRef] = useReactiveRef<number>(10);
  const [statValue, setstatValue, statValueRef] = useReactiveRef<number>(10);

  const { playerId, sportsCode } = useParams();
  const careerPlayerIds = useCareerPlayerIds();
  const isMultiTeam = careerPlayerIds.length > 1;

  // -------------------- FILTER HANDLERS --------------------

  const handleDropdownChange = (
    key: "OPP" | "POS" | "CLASS" | "PLAYER" | "RES" | "stat" | "teams",
    selectedValue: any
  ) => {
    setSelectedFilters((prev: any) => {
      const updated = { ...prev };
      let newValue = key === "RES" ? String(selectedValue.code) : selectedValue;

      const currentValues = updated[key] || [];

      if (currentValues.includes(newValue)) {
        updated[key] = currentValues.filter((v: any) => v !== newValue);
      } else {
        updated[key] = [...currentValues, newValue];
      }

      console.log("Updated Filters:", updated);
      return updated;
    });
  };

  const handleStartDateChange = (newDate: string) => {
    setStartDate(newDate);

    if (endDate && new Date(newDate) > new Date(endDate)) {
      setEndDate(null);
    }
  };

  const handleEndDateChange = (newDate: string) => {
    setEndDate(newDate);
  };

  const handleClearFilters = () => {
    setSelectedFilters({ teams: [] });
    setstatValue(10);
    setStreakLength(10);
    setStartDate(null);
    setEndDate(null);
    setPlayerStreaksRes(null);
    setCurrentPage(0);
  };

  const handleDeleteFilter = (filter: string) => {
    if (filter.startsWith("Start Date:")) {
      setStartDate(null);
      setPlayerStreaksRes(null);
      return;
    }

    if (filter.startsWith("End Date:")) {
      setEndDate(null);
      setPlayerStreaksRes(null);
      return;
    }

    if (selectedFilters?.teams?.includes(filter)) {
      setSelectedFilters({
        ...selectedFilters,
        teams: selectedFilters.teams.filter((team: string) => team !== filter),
      });
      return;
    }

    const isStatCategory = selectedFilters?.stat?.includes(filter);
    
    if (isStatCategory) {
      const categoryStats = playerStreaksMappingRes?.[filter] || [];
      const updatedStat = selectedFilters.stat.filter((s: string) => s !== filter);
      
      let updatedSubstat = selectedFilters.substat;
      if (selectedFilters.substat && categoryStats.includes(selectedFilters.substat)) {
        updatedSubstat = null;
      }
      
      setSelectedFilters({
        ...selectedFilters,
        stat: updatedStat,
        substat: updatedSubstat
      });
      setPlayerStreaksRes(null);
      return;
    }

    if (selectedFilters?.substat === filter) {
      setSelectedFilters({ ...selectedFilters, substat: null });
      setPlayerStreaksRes(null);
      return;
    }
  };

  // -------------------- GET COL DEF --------------------

  const formatColumnName = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getColDefs = () => {
    if (!playerStreaksRes?.PlayerStreaks?.length) return [];

    const keys = Object.keys(playerStreaksRes.PlayerStreaks[0]);

    return keys.map((key) => {
      const isDate = key.toLowerCase().includes("date");
      const formattedName = formatColumnName(key);

      const colDef: any = {
        field: key,
        headerName: formattedName,
        sortable: true,
      };

      if (isDate) {
        colDef.filter = "agDateColumnFilter";
        
        // FIXED: Robust date comparator for streaks
        colDef.comparator = (valueA: any, valueB: any) => {
            const toTimestamp = (dateStr: any): number | null => {
                if (!dateStr || dateStr === "-") return null;
                const str = String(dateStr).trim();

                let date;
                // Handle DD/MM/YYYY format explicitly if present
                if (str.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
                    const [day, month, year] = str.split('/').map(Number);
                    date = new Date(year, month - 1, day);
                } else {
                    // Handle ISO YYYY-MM-DD or other standard formats
                    date = new Date(str);
                }

                return isNaN(date.getTime()) ? null : date.getTime();
            };

            const timeA = toTimestamp(valueA);
            const timeB = toTimestamp(valueB);

            if (timeA === timeB) return 0; // handles both being null
            if (timeA === null) return 1; // sort nulls to the end (bottom)
            if (timeB === null) return -1;

            return timeA - timeB;
        };

        colDef.filterParams = {
          debounceMs: 500,
          suppressAndOrCondition: true,
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (cellValue == null) {
              return 0;
            }
            const cellDate = new Date(cellValue + "T00:00:00");
            if (isNaN(cellDate.getTime())) {
              return 0;
            }
            if (cellDate < filterLocalDateAtMidnight) {
              return -1;
            } else if (cellDate > filterLocalDateAtMidnight) {
              return 1;
            } else {
              return 0;
            }
          },
        };
        colDef.valueFormatter = (params: any) => {
          if (params.value) {
            const date = new Date(params.value + "T00:00:00");
            return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
          }
          return "-";
        };
      } else {
        colDef.filter = true;
        colDef.valueFormatter = (params: any) => params.value ?? "-";
      }

      return colDef;
    });
  };

  // -------------------- MAPPING CALL --------------------

  const getPlayerStreaksMapping = async () => {
    try {
      setLoading(true);
      const res: { data: PlayerStreaksMappingRes } = await api.get(
        PLAYER_STREAKS_MAPPING,
        {
          params: {
            SportCode: props.playerData?.SportCode,
            playerposition: props.playerData?.PlayerPosition,
          },
        }
      );

      console.log("Mapping Response:", res);
      setPlayerStreaksMappingRes(res.data);
    } catch (err) {
      console.error(err);
      openToast("Failed to load streak categories", "red");
    } finally {
      setLoading(false);
    }
  };

  // -------------------- MAIN API CALL --------------------

  const getPlayerStreaks = async () => {
    try {
      setLoading(true);
      setCurrentPage(0);

      const profiles =
        props.careerProfiles.length > 0
          ? props.careerProfiles
          : careerPlayerIds.map((id) => ({
              playerId: id,
              PlayerName: props.playerData?.PlayerName || "",
              TeamName: props.playerData?.TeamName || "",
              TeamCode: props.playerData?.TeamCode || "",
              PlayerPosition: props.playerData?.PlayerPosition || "",
              stat_mapping: props.playerData?.stat_mapping || {},
            }));

      const allStreaks: any[] = [];
      let totalCount = 0;

      for (const profile of profiles) {
        const payload = {
          Player_id: profile.playerId,
          SportCode: props.playerData?.SportCode,
          TeamCode: profile.TeamCode || props.playerData?.TeamCode,
          stat: selectedFilters.substat,
          stat_value: statValueRef.current,
          streak_length: streakLengthRef.current,
          start_date: startDate,
          end_date: endDate,
        };

        console.log("🔥 Payload Sent:", payload);

        const res: { data: PlayerStreaksRes } = await api.post(PLAYER_STREAKS, payload);
        console.log("🔥 Backend Response:", res.data);

        const rows = res.data?.PlayerStreaks ?? [];
        totalCount += res.data?.TotalCount ?? rows.length;
        for (const row of rows) {
          allStreaks.push({
            ...row,
            ...(isMultiTeam ? { TEAM: profile.TeamName || "" } : {}),
          });
        }
      }

      setPlayerStreaksRes({
        PlayerStreaks: allStreaks,
        TotalCount: totalCount || allStreaks.length,
      });
    } catch (err) {
      console.error("Error fetching streaks:", err);
      openToast("Failed to load player streaks", "red");
      setPlayerStreaksRes({ PlayerStreaks: [], TotalCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  // -------------------- EXPORT FUNCTION --------------------

  const handleExport = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!playerStreaksRes || playerStreaksRes.PlayerStreaks.length === 0) {
      openToast("No data to export", "red");
      return;
    }

    try {
      exportToXLSX(
        playerStreaksRes.PlayerStreaks,
        `Player_Streaks_${props.playerData?.PlayerName || 'data'}.xlsx`
      );
      openToast("Data exported successfully", "green");
    } catch (error) {
      console.error("Export error:", error);
      openToast("Failed to export data", "red");
    }
  };

  // -------------------- VALIDATION --------------------

  const handleApplyFilters = () => {
    let missing: string[] = [];

    if (!selectedFilters.substat) missing.push("Stat");
    if (statValueRef.current <= 0) missing.push("Stat Value (must be greater than 0)");
    if (streakLengthRef.current <= 0) missing.push("Streak Length (must be greater than 0)");

    if (missing.length > 0) {
      alert(`Please select/correct the following:\n\n${missing.join("\n")}`);
      return;
    }

    getPlayerStreaks();
  };

  // -------------------- INPUT HANDLERS --------------------

  const handleStatValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setstatValue(0);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        setstatValue(numValue);
      }
    }
  };

  const handleStreakLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setStreakLength(0);
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 0) {
        setStreakLength(numValue);
      }
    }
  };

  // -------------------- INITIAL LOAD --------------------

  useEffect(() => {
    setfilter_config({
      PageNumber: 1,
      PageSize: 1,
      entity: "Player",
    });

    setData({ teams: ["abc"] });
    getPlayerStreaksMapping();
  }, []);

  // -------------------- RETURN UI --------------------

  return (
    <div className="w-full flex flex-col gap-4">
      {/* FILTER DISPLAY */}
      <div className="flex flex-row gap-2 items-center justify-between flex-wrap">
        {([
          ...(selectedFilters?.teams ?? []),
          selectedFilters?.substat,
          ...(selectedFilters?.stat ?? []),
          startDate,
          endDate,
        ].filter(Boolean)?.length > 0) && (
          <p className="font-primary font-semibold text-lg">
            <div className="flex flex-row items-center gap-2 w-full">
              <div className="text-sm font-medium">Filters:-</div>
              <div className="flex gap-2 flex-wrap max-w-[40rem] px-2">
                {[
                  ...(selectedFilters?.teams ?? []),
                  selectedFilters?.substat,
                  ...(selectedFilters?.stat ?? []),
                  startDate ? `Start Date: ${startDate}` : null,
                  endDate ? `End Date: ${endDate}` : null,
                ]
                  .filter(Boolean)
                  .map((filter, idx) => (
                    <Pill key={idx} text={String(filter)} showCross onClick={() => handleDeleteFilter(String(filter))} />
                  ))}
              </div>
            </div>
          </p>
        )}

        {/* EXPORT BUTTON */}
        <div className="flex flex-row gap-2 items-center justify-end flex-wrap">
          <Button
            handleButtonClick={handleExport}
            color={team.colorCode}
            isDisabled={!playerStreaksRes || playerStreaksRes?.PlayerStreaks.length === 0}
            text="Export"
            icon="/export_icon.png"
            type=""
            iconAllignment="left"
            size=""
          />
        </div>
      </div>

      <hr className="h-px my-0.5 bg-[#AEAEAE] border-0" />

      {/* MAIN LAYOUT */}
      <div className="flex flex-row gap-5 mb-2">
        {/* FILTER PANEL */}
        {data?.teams?.length > 0 && (
          <div className="max-w-[270px] w-full bg-white rounded-[10px] border border-[#DEE7F4] p-4 mt-1.5 flex flex-col gap-2">
            <div className="flex flex-row justify-between">
              <p className="text-md font-semibold font-primary">Filters</p>
              <p
                className="text-sm font-semibold font-primary cursor-pointer"
                style={{ color: team.colorCode }}
                onClick={handleClearFilters}
              >
                Clear All
              </p>
            </div>

            <hr className="h-px my-0.5 bg-[#E5E5E5]" />

            {isMultiTeam && (
              <>
                <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">Team</p>
                <MultiSelectDropdown
                  options={[
                    ...new Set([
                      ...props.careerProfiles.map((p) => p.TeamName).filter(Boolean),
                      ...((playerStreaksRes?.PlayerStreaks ?? []) as any[])
                        .map((r) => r.TEAM)
                        .filter(Boolean),
                    ]),
                  ]}
                  options2={[]}
                  def="Select Team"
                  setSelectedOption={handleDropdownChange}
                  selectedOption={selectedFilters}
                  filterKey="teams"
                  disabled={false}
                />
              </>
            )}

            {/* CATEGORY */}
            <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">Category</p>
            <MultiSelectDropdown
              options={Object.keys(playerStreaksMappingRes ?? {})}
              options2={[]}
              def="Select Category"
              setSelectedOption={handleDropdownChange}
              selectedOption={selectedFilters}
              filterKey="stat"
              disabled={false}
            />

            {/* STAT */}
            <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">Stat</p>
            <DropdownSelect
              options={selectedFilters?.stat?.map((s: string) => playerStreaksMappingRes?.[s]).flat() ?? []}
              options2={[]}
              def="Select Stat"
              setSelectedOption={(option) => setSelectedFilters({ ...selectedFilters, substat: option })}
              selectedOption={selectedFilters.substat}
            />

            {/* STAT VALUE */}
            <div>
              <div className="text-sm font-secondary font-semibold text-[#5B5B5B] pb-2">Stat Value</div>
              <input
                type="number"
                className="w-full border rounded-lg px-5 py-2.5"
                value={statValue === 0 ? '' : statValue}
                placeholder="Enter Stat (default: 10)"
                onChange={handleStatValueChange}
                min="1"
              />
            </div>

            {/* STREAK LENGTH */}
            <div>
              <div className="text-sm font-secondary font-semibold text-[#5B5B5B] pb-2">Streak Length</div>
              <input
                type="number"
                className="w-full border rounded-lg px-5 py-2.5"
                value={streakLength === 0 ? '' : streakLength}
                placeholder="Enter Streak Length (default: 10)"
                onChange={handleStreakLengthChange}
                min="1"
              />
            </div>

            {/* DATE RANGE */}
            <p className="text-sm font-secondary font-semibold text-[#5B5B5B]">Date Range</p>
            <div className="flex gap-2">
              <Datepicker
                date={startDate || ""}
                setDate={handleStartDateChange}
                disabled={false}
                type="DATE_MIN"
                className="w-[114px]"
              />

              <Datepicker
                date={endDate || ""}
                setDate={handleEndDateChange}
                disabled={!startDate}
                type="DATE_MAX"
                className="w-[114px]"
                startDate={startDate || undefined}
              />
            </div>

            {/* APPLY BUTTON */}
            <button
              className="py-3 bg-[#A8042D] text-white rounded-lg text-sm font-medium hover:bg-[#8a0325] transition-colors"
              onClick={handleApplyFilters}
            >
              View Results
            </button>
          </div>
        )}

        {/* TABLE - NOW WITH CLIENT-SIDE SORTING ON ALL DATA */}
        <div className="flex-grow">
          <AgGridReact
            rowData={(playerStreaksRes?.PlayerStreaks || []).filter((row: any) => {
              if (!isMultiTeam || !selectedFilters?.teams?.length) return true;
              return selectedFilters.teams.includes(row.TEAM);
            })}
            columnDefs={getColDefs()}
            theme={themeQuartz}
            loadingOverlayComponent={Spinner}
            loading={loading}
            defaultColDef={{
              flex: 1,
              sortable: true,
              filter: true,
              valueFormatter: (params) => params.value ?? "-",
              comparator: (valueA, valueB) => {
                const isADash = valueA === "-" || valueA === null || valueA === undefined;
                const isBDash = valueB === "-" || valueB === null || valueB === undefined;
                
                if (isADash && isBDash) return 0;
                if (isADash) return 1;
                if (isBDash) return -1;
                
                // STRICT CHECK: Only parse as number if string is purely numeric (no hyphens)
                const isNumericString = (val: any) => /^-?\d*\.?\d+$/.test(String(val));

                if (isNumericString(valueA) && isNumericString(valueB)) {
                  return parseFloat(String(valueA)) - parseFloat(String(valueB));
                }
                
                return String(valueA).localeCompare(String(valueB));
              },
            }}
            pagination={true}
            paginationPageSize={itemsPerPage}
            paginationPageSizeSelector={[10, 25, 50, 100]}
            onPaginationChanged={(params) => {
              const newPageSize = params.api.paginationGetPageSize();
              if (newPageSize !== itemsPerPage) {
                setItemsPerPage(newPageSize);
              }
            }}
          />

        </div>
      </div>
    </div>
  );
};